import React, { useState, useEffect, useCallback, useRef } from "react"
import ReportingDisplay from "features/Reporting/components/ReportingDisplay/ReportingDisplay.component"
import IntegratedReportSelector from "features/Reporting/components/IntegratedReportSelector/IntegratedReportSelector.component"
import ReportingMenuBar from "features/Reporting/components/ReportingMenuBar/ReportingMenuBar.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import { message } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"

import { useEngine } from "hooks/api/useEngine"
import { useDataImport } from "features/DataImport/api/useDataImport"
import { useDashboard } from "features/Reporting/hooks/Dashboard/useDashboard"

import handleSequenceError from "utils/handleSequenceError"
import { createSlideId } from "utils/idGenerators"
import { prepareElementsForExport } from "utils/prepareElementsForExport"

import { ReportTableDisplay } from "features/Reporting/components/ReportTableDisplay"
import ReportingTypeSwitch from "features/Reporting/components/ReportingTypeSwitch/ReportingTypeSwitch.component"
import ReportingGlobalFilter from "features/Reporting/components/ReportingGlobalFilter/ReportingGlobalFilter.component"
import NoReportSelected from "features/Reporting/components/NoReportSelected/NoReportSelected.component"
import CustomQueryModal from "features/Reporting/components/CustomQueryModal/CustomQueryModal.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"

import { useProjectStore, Project } from "stores/projectStore"
import {
  ReportingDashboardConfig,
  SlideForExport,
  TableDataCache,
  PrepareTablesResult,
  ExcelReportingRef,
  ReportingType,
  DashboardConfigChange,
} from "types/ReportingDashboard/reporting.types"
import { CodebookData } from "types/codebook.types"
import { DATAFILE_TYPE } from "types/dataImport.types"
import { DataElementConfig } from "types/ReportingDashboard/elements.types"
import { QueryInstance } from "types/engine.types"
import {
  EngineQueryResult,
  EngineTableDumpContent,
  RTableContent,
} from "types/engineOutput.types"
import { AxiosResponse } from "axios"

// Define missing types locally since they're not in the provided type files
interface AutoRunResult {
  success: boolean
  result?: AxiosResponse<unknown>
  error?: string
}

interface CodebookResult {
  success: boolean
  codebook?: CodebookData
  response?: AxiosResponse<unknown>
  error?: Error
}

// Helper function to fetch codebook with retry logic
const fetchCodebookWithRetry = async (
  RunEngineQuery: (
    instanceId: QueryInstance,
    script: string,
    log: boolean
  ) => Promise<AxiosResponse<unknown>>,
  instanceId: QueryInstance,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<CodebookResult> => {
  let attempt = 0
  let delay = initialDelay

  while (attempt < maxRetries) {
    try {
      const codebookRes = await RunEngineQuery(instanceId, "CODEBOOK", false)
      const engineResult = codebookRes.data as EngineQueryResult

      if (engineResult?.result?.output?.[0]?.content) {
        const outputItem = engineResult.result.output[0]
        // For CODEBOOK, the content should be an EngineTableDumpContent
        if (outputItem.content && "value" in outputItem.content) {
          const tableContent = outputItem.content as EngineTableDumpContent
          return {
            success: true,
            codebook: tableContent.value as CodebookData,
            response: codebookRes,
          }
        }
      }
    } catch (error) {
      console.error(`Error in codebook fetch attempt ${attempt + 1}:`, error)
    }

    await new Promise((resolve) => setTimeout(resolve, delay))
    delay *= 2
    attempt++
  }

  return { success: false, codebook: undefined, response: undefined }
}

const ReportingContainer: React.FC = () => {
  const project: Project = useProjectStore((state) => state.project)
  const {
    getDashboardFile,
    saveDashboardFile,
    isSaving: isSavingFile,
  } = useDashboard()

  const initialSlideId: string = createSlideId() // Use a function that generates a unique ID
  const initialGlobalConfig: ReportingDashboardConfig = {
    autoRun: { enabled: false, script: "" },
    dashboard: {
      slides: [
        {
          id: initialSlideId,
          templateId: null,
          slideElements: [],
          slideTableConfigs: {},
          backgroundColor: "#ffffff", // Default white background
        },
      ],
      templates: {},
      defaultBackgroundColor: "#ffffff", // Default background color for new slides
    },
    excelReport: {
      tabs: [
        {
          key: "excelTab-1", // Ensure keys are unique enough
          label: "Table 1",
          rowVariables: [],
          columnVariables: [],
          selectedOperators: [
            STATISTIC_TYPE.N,
            STATISTIC_TYPE.BIG_N,
            STATISTIC_TYPE.ROW_PERCENT,
            STATISTIC_TYPE.COL_PERCENT,
            STATISTIC_TYPE.INDEX,
            STATISTIC_TYPE.PERC_PERCENT,
          ],
          // Add the new fields for removed elements
          removedElements: {
            columns: [],
            rows: [],
          },
          persistentRows: [],
          tableOperationType: TABLE_OPERATION_TYPE.XTAB, // Default to XTAB
        },
      ],
    },
    filter: "",
    lastModified: Date.now(),
  }

  const [config, setConfig] =
    useState<ReportingDashboardConfig>(initialGlobalConfig)
  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  const configRef = useRef<ReportingDashboardConfig>(config) // For callbacks

  // Runtime cache for table data (not saved to config)
  const [tableDataCache, setTableDataCache] = useState<TableDataCache>({}) // Callback to update table data cache
  const updateTableDataCache = useCallback(
    (elementId: string, tableData: RTableContent | null) => {
      setTableDataCache((prev) => {
        if (tableData === null) {
          const { [elementId]: _removed, ...rest } = prev
          return rest
        }
        return {
          ...prev,
          [elementId]: tableData,
        }
      })
    },
    []
  )

  // Wrapper function to handle EngineQueryResult from TableElement components
  const handleTableDataUpdate = useCallback(
    (elementId: string, engineResult: EngineQueryResult | null) => {
      if (engineResult === null) {
        updateTableDataCache(elementId, null)
        return
      }

      // Extract RTableContent from EngineQueryResult
      const tableContent = engineResult?.result?.output?.[0]
        ?.content as RTableContent
      if (tableContent) {
        updateTableDataCache(elementId, tableContent)
      } else {
        console.warn(
          `No table content found in engine result for element ${elementId}`
        )
        updateTableDataCache(elementId, null)
      }
    },
    [updateTableDataCache]
  )

  useEffect(() => {
    configRef.current = config
  }, [config])

  const [currentSlidesForExport, setCurrentSlidesForExport] = useState<
    SlideForExport[]
  >([])

  const { initialiseData, isInitialisingData, RunEngineQuery } = useEngine()
  const { getDataFilesUploaded, isGettingDataFilesUploaded } = useDataImport()
  const { getProjectDashboards } = useDashboard()

  // Core state
  const [isCodebookLoading, setIsCodebookLoading] = useState<boolean>(false)
  const [dataUploaded, setDataUploaded] = useState<boolean>(false)
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true)
  const [selectedReport, setSelectedReport] = useState<string | null>(null)
  const [reportingType, setReportingType] = useState<ReportingType>("dashboard")
  const [availableReports, setAvailableReports] = useState<string[]>([])

  const [codebook, setCodebook] = useState<CodebookData | null>(null)
  const [queryInstance, setQueryInstance] = useState<QueryInstance | null>(null)
  const [isCustomQueryModalVisible, setIsCustomQueryModalVisible] =
    useState<boolean>(false)

  const excelReportingRef = useRef<ExcelReportingRef>(null)

  // Holds the project id an engine query instance was requested for. Guards
  // against duplicate initialise-data calls (effect re-fires, StrictMode
  // double effects): every duplicate creates another engine instance holding
  // a full copy of the project's dataset, which lingers server-side.
  const initialisedProjectRef = useRef<string | null>(null)

  const [isAutoRunningScript, setIsAutoRunningScript] = useState<boolean>(false)
  const [hasAutoRunExecuted, setHasAutoRunExecuted] = useState<boolean>(false)

  // This state is for ReportingDisplay internal active slide, managed here now.
  const [activeDashboardSlideId, setActiveDashboardSlideId] = useState<
    string | null
  >(config.dashboard.slides[0]?.id)

  // --- Configuration Management ---
  const loadReportConfig = useCallback(async (): Promise<void> => {
    if (!selectedReport || !project?.id) {
      setConfig(initialGlobalConfig) // Reset to default if no report/project
      setActiveDashboardSlideId(initialGlobalConfig.dashboard.slides[0]?.id)
      setHasUnsavedChanges(false)
      setTableDataCache({}) // Clear cache when no report
      return
    }

    setIsLoadingConfig(true)
    try {
      const response = await getDashboardFile(
        project.id,
        project.Created_by,
        selectedReport,
        project.Created_by
      )

      if (response?.data) {
        let loadedConfigData: ReportingDashboardConfig
        try {
          loadedConfigData =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data

          // Validate and set default structure if needed
          const newConfig: ReportingDashboardConfig = {
            ...initialGlobalConfig,
            ...loadedConfigData,
          } // Merge with defaults

          // Ensure dashboard structure
          if (
            !newConfig.dashboard ||
            !Array.isArray(newConfig.dashboard.slides) ||
            newConfig.dashboard.slides.length === 0
          ) {
            newConfig.dashboard = { ...initialGlobalConfig.dashboard }
          }
          if (
            !newConfig.dashboard.templates ||
            typeof newConfig.dashboard.templates !== "object"
          ) {
            newConfig.dashboard.templates = {
              ...initialGlobalConfig.dashboard.templates,
            }
          }

          // Ensure default background color exists
          if (!newConfig.dashboard.defaultBackgroundColor) {
            newConfig.dashboard.defaultBackgroundColor = "#ffffff"
          }

          // Migrate slides to ensure they have background colors
          newConfig.dashboard.slides = newConfig.dashboard.slides.map(
            (slide) => ({
              ...slide,
              backgroundColor:
                slide.backgroundColor ||
                newConfig.dashboard.defaultBackgroundColor ||
                "#ffffff",
            })
          )

          // Migrate templates to ensure they have background colors
          Object.keys(newConfig.dashboard.templates).forEach((templateId) => {
            const template = newConfig.dashboard.templates[templateId]
            if (!template.backgroundColor) {
              newConfig.dashboard.templates[templateId] = {
                ...template,
                backgroundColor:
                  newConfig.dashboard.defaultBackgroundColor || "#ffffff",
              }
            }
          })

          // Ensure autorun structure
          if (!newConfig.autoRun || typeof newConfig.autoRun !== "object") {
            newConfig.autoRun = { ...initialGlobalConfig.autoRun }
          }

          // Ensure excelReport structure
          if (
            !newConfig.excelReport ||
            !Array.isArray(newConfig.excelReport.tabs)
          ) {
            newConfig.excelReport = { ...initialGlobalConfig.excelReport }
          }
          if (newConfig.excelReport.tabs.length === 0) {
            newConfig.excelReport.tabs = [
              ...initialGlobalConfig.excelReport.tabs,
            ]
          } else {
            // MIGRATE legacy tabs that don't have required properties
            newConfig.excelReport.tabs = newConfig.excelReport.tabs.map(
              (tab) => {
                const migratedTab = {
                  ...tab,
                  // Ensure removed elements structure exists
                  removedElements: tab.removedElements || {
                    columns: [],
                    rows: [],
                  },
                  // Ensure persistent rows exists
                  persistentRows: tab.persistentRows || [],
                  // CRITICAL: Ensure tableOperationType exists for all tabs
                  tableOperationType:
                    tab.tableOperationType || TABLE_OPERATION_TYPE.XTAB,
                }

                return migratedTab
              }
            )
          }
          // Ensure filter is a string
          if (typeof newConfig.filter !== "string") {
            newConfig.filter = initialGlobalConfig.filter
          }

          // Set the migrated configuration
          setConfig({ ...newConfig, lastModified: Date.now() })
          setActiveDashboardSlideId(newConfig.dashboard.slides[0]?.id) // For ReportingDisplay
          setHasUnsavedChanges(false)
          setTableDataCache({}) // Clear cache when loading new config
          notify.toast.success("Report configuration loaded successfully")
        } catch (parseError) {
          console.error("Error parsing report configuration:", parseError)
          notify.toast.error("Error loading report configuration, using defaults.")
          setConfig(initialGlobalConfig)
          setActiveDashboardSlideId(initialGlobalConfig.dashboard.slides[0]?.id)
          setTableDataCache({})
        }
      } else {
        // No existing config, use initial
        setConfig(initialGlobalConfig)
        setActiveDashboardSlideId(initialGlobalConfig.dashboard.slides[0]?.id)
        setHasUnsavedChanges(false)
        setTableDataCache({})
        notify.toast.info(
          "No existing configuration found, starting with a new one."
        )
      }
    } catch (error) {
      console.error("Error loading report configuration:", error)
      notify.toast.error("Failed to load report configuration, using defaults.")
      setConfig(initialGlobalConfig)
      setActiveDashboardSlideId(initialGlobalConfig.dashboard.slides[0]?.id)
      setTableDataCache({})
    } finally {
      setIsLoadingConfig(false)
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, project?.id, getDashboardFile, initialGlobalConfig])

  useEffect(() => {
    loadReportConfig()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, project?.id]) // Reload config when report or project changes

  const saveConfiguration = useCallback(
    async (
      configToSaveParam?: ReportingDashboardConfig,
      showSuccessMessage: boolean = true
    ): Promise<boolean> => {
      if (!selectedReport || !project?.id) {
        if (showSuccessMessage) notify.toast.error("No report selected to save")
        return false
      }

      // If a specific config object is passed (configToSaveParam), use it.
      // Otherwise, fall back to configRef.current for general saves (e.g., from menu bar).
      const finalConfigToSave: ReportingDashboardConfig = configToSaveParam
        ? { ...configToSaveParam, lastModified: Date.now() } // Ensure timestamp if passed directly
        : { ...configRef.current, lastModified: Date.now() } // Fallback uses ref

      try {
        await saveDashboardFile(
          project.id,
          project.Created_by,
          JSON.stringify(finalConfigToSave),
          selectedReport,
          true // overwrite
        )
        // Never restore the pre-save snapshot: the save was in flight while
        // the user may have kept editing (dragging elements etc.), and
        // replacing state with `finalConfigToSave` would revert those edits
        // (elements bouncing back to old positions). Only sync the timestamp.
        setConfig((prev) => ({
          ...prev,
          lastModified: finalConfigToSave.lastModified,
        }))
        setHasUnsavedChanges(false)

        if (showSuccessMessage) {
          notify.toast.success("Report configuration saved successfully")
        }
        return true
      } catch (error) {
        console.error("Error saving report configuration:", error)
        if (showSuccessMessage)
          notify.toast.error("Failed to save report configuration")
        return false
      }
    },
    [selectedReport, project?.id, project?.Created_by, saveDashboardFile]
  )

  const handleDashboardConfigChange = useCallback(
    (change: DashboardConfigChange) => {
      setConfig((prevConfig) => {
        // Functional updaters compute their partial from the CURRENT state,
        // so async writers (debounced image-config saves) can't clobber
        // concurrent edits. A null result aborts with no state change.
        const partial =
          typeof change === "function" ? change(prevConfig.dashboard) : change
        if (partial === null) return prevConfig
        return {
          ...prevConfig,
          dashboard: {
            ...prevConfig.dashboard, // Start with existing dashboard state
            ...partial, // Overlay the changes (e.g., new slides OR new templates OR both)
          },
          lastModified: Date.now(),
        }
      })
      setHasUnsavedChanges(true)
    },
    []
  )

  const handleExcelReportConfigChange = useCallback(
    (
      newExcelReportTabsConfig: ReportingDashboardConfig["excelReport"]["tabs"]
    ) => {
      setConfig((prevConfig) => ({
        ...prevConfig,
        excelReport: {
          ...prevConfig.excelReport,
          tabs: newExcelReportTabsConfig,
        },
        lastModified: Date.now(),
      }))
      setHasUnsavedChanges(true)
    },
    []
  )

  const handleFilterChange = useCallback((newFilter: string) => {
    setConfig((prevConfig) => ({
      ...prevConfig,
      filter: newFilter,
      lastModified: Date.now(),
    }))
    setHasUnsavedChanges(true)
  }, [])

  const handleTableConfigUpdate = useCallback(
    (elementId: string, config: DataElementConfig) => {
      setConfig((prevConfig) => {
        const currentSlides = prevConfig.dashboard.slides || []
        const currentTemplates = prevConfig.dashboard.templates || {}

        // Find which slide or template contains this element and update the config
        let updatedSlides = [...currentSlides]
        const updatedTemplates = { ...currentTemplates }
        let configUpdated = false

        // Check slides
        updatedSlides = updatedSlides.map((slide) => {
          if (slide.slideTableConfigs && slide.slideTableConfigs[elementId]) {
            configUpdated = true
            return {
              ...slide,
              slideTableConfigs: {
                ...slide.slideTableConfigs,
                [elementId]: config,
              },
            }
          }
          return slide
        })

        // Check templates
        Object.keys(updatedTemplates).forEach((templateId) => {
          const template = updatedTemplates[templateId]
          if (template.tableConfigs && template.tableConfigs[elementId]) {
            configUpdated = true
            updatedTemplates[templateId] = {
              ...template,
              tableConfigs: {
                ...template.tableConfigs,
                [elementId]: config,
              },
            }
          }
        })

        if (configUpdated) {
          return {
            ...prevConfig,
            dashboard: {
              ...prevConfig.dashboard,
              slides: updatedSlides,
              templates: updatedTemplates,
            },
            lastModified: Date.now(),
          }
        }

        return prevConfig
      })
      setHasUnsavedChanges(true)
    },
    []
  )

  // --- End Configuration Management ---

  const getExcelTablesData = useCallback(async () => {
    try {
      if (!excelReportingRef.current) {
        notify.toast.warning(
          "Excel tables are not ready yet. Please try again in a moment."
        )
        return []
      }
      if (typeof excelReportingRef.current.getExcelTablesData === "function") {
        const tablesData = await excelReportingRef.current.getExcelTablesData()
        return tablesData || []
      } else {
        notify.toast.warning("Excel export functionality is not ready yet.")
        return []
      }
    } catch (error) {
      console.error("Error in getExcelTablesData wrapper:", error)
      notify.toast.error("An error occurred while preparing Excel data.")
      return []
    }
  }, [])

  const handleSlidesForExportUpdate = useCallback(
    (newSlides: SlideForExport[]) => {
      setCurrentSlidesForExport(newSlides)
    },
    []
  )

  // Now using the extracted utility function
  const prepareElementsForExportCallback = useCallback(
    async (forceRefreshAll: boolean = false): Promise<PrepareTablesResult> => {
      if (!queryInstance || !codebook) {
        console.warn(
          "prepareElementsForExport: Missing queryInstance or codebook. Cannot prepare elements."
        )
        return { newlyFetchedData: {}, generatedChartImages: {} }
      }

      return await prepareElementsForExport({
        config: configRef.current,
        queryInstance,
        tableDataCache,
        forceRefreshAll,
        RunEngineQuery,
        updateTableDataCache,
        handleTableConfigUpdate,
      })
    },
    [
      queryInstance,
      codebook,
      tableDataCache,
      RunEngineQuery,
      updateTableDataCache,
      handleTableConfigUpdate,
    ]
  )

  const onReportSelect = (report: string | null) => {
    setSelectedReport(report)
    setHasAutoRunExecuted(false)
  }

  const pageLoading: boolean = isGettingDataFilesUploaded || isInitialisingData

  useEffect(() => {
    const handleAutoRun = async (): Promise<void> => {
      if (
        queryInstance &&
        config.autoRun.enabled &&
        config.autoRun.script &&
        dataUploaded &&
        !isAutoRunningScript &&
        !hasAutoRunExecuted
      ) {
        setHasAutoRunExecuted(true)

        const autoRunResult = await runAutoScript(
          queryInstance,
          config.autoRun.script
        )

        if (autoRunResult.success) {
          await fetchAndSetCodebook(queryInstance, false)
        }
      }
    }
    handleAutoRun()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryInstance,
    config.autoRun.enabled,
    config.autoRun.script,
    dataUploaded,
    hasAutoRunExecuted,
    isAutoRunningScript, // Added to prevent race conditions
  ])

  useEffect(() => {
    if (project && project.id) {
      getInitialData()
    } else {
      initialisedProjectRef.current = null
      setIsDataLoading(false)
      setDataUploaded(false)
      setCodebook(null)
      setQueryInstance(null)
      setAvailableReports([])
      setConfig(initialGlobalConfig) // Reset config
      setActiveDashboardSlideId(initialGlobalConfig.dashboard.slides[0]?.id)
      setHasAutoRunExecuted(false)
      setTableDataCache({}) // Clear cache
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]) // Key on the id, not the object: a re-fetched project is
  // a new object reference every time, and each extra effect fire used to
  // spin up (and leak) another engine query instance.

  const handleAutoRunCheckboxChange = useCallback(
    async (enabled: boolean): Promise<void> => {
      const newConfig: ReportingDashboardConfig = {
        ...configRef.current, // Start with the most up-to-date config from the ref
        autoRun: {
          ...configRef.current.autoRun, // Preserve other autoRun properties like 'script'
          enabled: enabled, // Update only the enabled flag
        },
      }

      setConfig(newConfig)
      setHasUnsavedChanges(true)

      const saveSuccess = await saveConfiguration(newConfig, false)
      if (saveSuccess) {
        notify.toast.success(
          `Auto-run ${enabled ? "enabled" : "disabled"}. Configuration saved.`,
          2
        )
      }
    },
    [saveConfiguration] // Dependency: saveConfiguration. configRef is a ref.
  )

  const handleScriptContentSave = useCallback(
    async (script: string): Promise<void> => {
      const newConfig: ReportingDashboardConfig = {
        ...configRef.current, // Start with the most up-to-date config from the ref
        autoRun: {
          ...configRef.current.autoRun, // Preserve other autoRun properties like 'enabled'
          script: script, // Update only the script
        },
      }

      setConfig(newConfig)
      setHasUnsavedChanges(true)

      await saveConfiguration(newConfig, false)
    },
    [saveConfiguration] // Dependency: saveConfiguration. configRef is a ref.
  )

  const runAutoScript = useCallback(
    async (
      instanceId: QueryInstance,
      script: string
    ): Promise<AutoRunResult> => {
      if (!instanceId || !script) return { success: false }
      setIsAutoRunningScript(true)
      try {
        const result = await RunEngineQuery(
          instanceId,
          "RESET\n" + script,
          false
        )
        const engineResult = result.data as EngineQueryResult
        if (
          result?.status === 200 &&
          engineResult?.result?.log?.[0] === "Script successfully executed."
        ) {
          notify.toast.success("Preprocessing script run automatically.", 2)
          return { success: true, result }
        } else {
          const engineError =
            engineResult?.result?.output?.[0]?.message || "Unknown engine error"
          const statusText = result?.status ? ` (Status: ${result.status})` : ""
          console.error(
            `Auto-run script failed: ${engineError}${statusText}`,
            result
          )
          notify.toast.error(`Auto-run script failed: ${engineError}`, 5)
          return { success: false, error: engineError }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error)
        console.error("Error during auto-run script execution:", error)
        notify.toast.error(`Error during auto-run script: ${errorMessage}`, 4)
        return { success: false, error: errorMessage }
      } finally {
        setIsAutoRunningScript(false)
      }
    },
    [RunEngineQuery] // Removed message from deps
  )

  const fetchAndSetCodebook = useCallback(
    async (
      instanceId: QueryInstance,
      notifyUser: boolean = true
    ): Promise<CodebookResult> => {
      if (!instanceId) return { success: false }
      setIsCodebookLoading(true)
      if (notifyUser)
        // keyed message flow — notify.toast has no replace-in-place; keep antd message here
        message.loading({
          content: "Loading codebook...",
          key: "codebookLoading",
        })
      try {
        const {
          success,
          codebook: fetchedCodebook,
          response,
        } = await fetchCodebookWithRetry(RunEngineQuery, instanceId)
        if (
          success &&
          response &&
          handleSequenceError(
            (response.data as EngineQueryResult).result,
            "Error Loading Codebook!"
          )
        ) {
          setCodebook(fetchedCodebook!)
          if (notifyUser)
            message.success({
              content: "Codebook loaded successfully",
              key: "codebookLoading",
              duration: 2,
            })
          return { success: true, codebook: fetchedCodebook }
        } else {
          console.error("Failed to load codebook after retries")
          if (notifyUser)
            message.error({
              content: "Failed to load codebook. Please try again.",
              key: "codebookLoading",
              duration: 3,
            })
          return { success: false }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error)
        console.error("Error fetching codebook:", error)
        if (notifyUser)
          message.error({
            content: `Error loading codebook: ${errorMessage}`,
            key: "codebookLoading",
            duration: 3,
          })
        return { success: false, error: error as Error }
      } finally {
        setIsCodebookLoading(false)
        if (notifyUser) message.destroy("codebookLoading")
      }
    },
    [RunEngineQuery]
  )

  const loadAvailableReports = useCallback(async (): Promise<void> => {
    if (!project || !project.id) return // Guard against missing project
    try {
      const availableDashboards = await getProjectDashboards(
        project.id,
        project.Created_by
      )
      const reports = availableDashboards.data

      if (typeof reports === "string") {
        const reportNames = reports.split("\n").filter((name) => name.trim())
        setAvailableReports(reportNames)
      } else if (Array.isArray(reports)) {
        const reportNames = (reports as string[]).map((report: string) =>
          typeof report === "object" && report && "name" in report
            ? (report as { name: string }).name
            : String(report)
        )
        setAvailableReports(reportNames)
      } else if (typeof reports === "number") {
        setAvailableReports([String(reports)]) // Convert number to string and put in array
      } else {
        setAvailableReports([])
      }
    } catch (error) {
      console.error("Error loading available reports:", error)
      setAvailableReports([])
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, getProjectDashboards, project?.Created_by]) // Use project.id for stability

  // Callback for IntegratedReportSelector to refresh ONLY the list of reports
  const handleRefreshReportListOnly = useCallback(async (): Promise<void> => {
    await loadAvailableReports()
  }, [loadAvailableReports])

  const getInitialData = useCallback(async (): Promise<void> => {
    if (!project || !project.id) {
      setIsDataLoading(false)
      return
    }
    if (initialisedProjectRef.current === project.id) {
      // Already initialised (or initialising) for this project — a duplicate
      // call would create another engine query instance.
      return
    }
    initialisedProjectRef.current = project.id
    setIsDataLoading(true)
    setDataUploaded(false)
    setCodebook(null)
    setQueryInstance(null)
    setAvailableReports([]) // Reset reports list
    setConfig(initialGlobalConfig) // Reset config
    setIsAutoRunningScript(false)
    setHasAutoRunExecuted(false)
    setTableDataCache({}) // Clear cache
    try {
      const fetchRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.DQD,
        "size"
      )
      if (typeof fetchRes.data !== "number") {
        // No data file uploaded — no instance was created, allow a later call
        // (e.g. after an upload) to initialise.
        initialisedProjectRef.current = null
        setDataUploaded(false)
        setAvailableReports([])
        return // No finally block here to allow sequential flow
      }
      const initialiseRes = await initialiseData(project.id)
      if (
        !initialiseRes?.data ||
        !handleSequenceError(initialiseRes.data, "Error Initializing Data!")
      ) {
        // No instance was created — allow a later retry.
        initialisedProjectRef.current = null
        setDataUploaded(false)
        return
      }
      const instanceId = initialiseRes.data as QueryInstance
      setQueryInstance(instanceId)
      await new Promise((resolve) => setTimeout(resolve, 500)) // Brief pause
      const initialCodebookResult = await fetchAndSetCodebook(instanceId, false)
      if (!initialCodebookResult.success) {
        notify.toast.error("Failed to load codebook.")
        setDataUploaded(false)
      } else {
        setDataUploaded(true) // Set dataUploaded only after successful codebook
      }
      await loadAvailableReports()
    } catch (error) {
      // Allow an explicit retry (e.g. the refresh button) to re-initialise.
      initialisedProjectRef.current = null
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      console.error("Error in getInitialData:", error)
      setDataUploaded(false)
      notify.toast.error(`Error loading project data: ${errorMessage}`)
    } finally {
      setIsDataLoading(false)
      setIsCodebookLoading(false) // Ensure this is reset
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    project?.id, // Use project.id for stability
    project?.Created_by,
    getDataFilesUploaded,
    initialiseData,
    fetchAndSetCodebook,
    loadAvailableReports,
  ])

  const showCustomQueryModal = (): void => setIsCustomQueryModalVisible(true)
  const handleCustomQueryModalClose = (): void =>
    setIsCustomQueryModalVisible(false)

  const handleCustomScriptSuccess = useCallback(async (): Promise<void> => {
    if (queryInstance) {
      await fetchAndSetCodebook(queryInstance, true)
    }
  }, [queryInstance, fetchAndSetCodebook])

  const handleDataRefresh = useCallback((): void => {
    if (project && project.id) {
      // Explicit refresh: release the duplicate guard so a fresh engine
      // instance is created for the (possibly re-uploaded) data.
      initialisedProjectRef.current = null
      setSelectedReport(null)
      getInitialData()
    }
  }, [project, getInitialData])

  const isBusyLoading: boolean =
    pageLoading ||
    isDataLoading ||
    isAutoRunningScript ||
    isCodebookLoading ||
    isLoadingConfig

  if (pageLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader fontSize={48} color="#36C3ED" />
        {isGettingDataFilesUploaded
          ? "Checking Data File..."
          : isInitialisingData
          ? "Initializing Data File..."
          : "Loading..."}
      </div>
    )
  }

  return (
    <div id="reporting" className="flex flex-col h-full">
      <div className="flex flex-row w-full gap-1">
        <div className="flex flex-row gap-1 px-2">
          <IntegratedReportSelector
            onReportSelect={onReportSelect}
            selectedReport={selectedReport}
            dataUploaded={dataUploaded}
            availableReports={availableReports}
            onDataRefresh={handleDataRefresh}
            project={project}
            handleRefreshReportListOnly={handleRefreshReportListOnly}
          />
          {dataUploaded && codebook && selectedReport && (
            <ReportingTypeSwitch setReportingType={setReportingType} />
          )}
        </div>
        <div className="flex flex-row gap-1 ml-auto px-2">
          {queryInstance && dataUploaded && selectedReport && (
            <>
              {codebook && (
                <ReportingGlobalFilter
                  setFilter={handleFilterChange}
                  codebook={codebook}
                  filter={config.filter}
                />
              )}
              <CustomQueryModal
                isVisible={isCustomQueryModalVisible}
                onClose={handleCustomQueryModalClose}
                queryInstance={queryInstance}
                onScriptRunSuccess={handleCustomScriptSuccess}
                showCustomQueryModal={showCustomQueryModal}
                isAutoRunEnabled={config.autoRun.enabled}
                onAutoRunChange={handleAutoRunCheckboxChange}
                initialScriptContent={config.autoRun.script}
                onScriptContentSave={handleScriptContentSave}
                codebook={codebook}
              />
              {codebook && (
                <ReportingMenuBar
                  slides={currentSlidesForExport}
                  reportingType={reportingType}
                  getExcelTablesData={getExcelTablesData}
                  saveConfiguration={saveConfiguration}
                  hasUnsavedChanges={hasUnsavedChanges}
                  isSaving={isSavingFile}
                  currentConfig={config}
                  prepareTablesForExport={prepareElementsForExportCallback}
                  tableDataCache={tableDataCache}
                />
              )}
            </>
          )}
        </div>
      </div>

      {!dataUploaded ? (
        !isDataLoading ? (
          <EmptyState message="No Data File Found! Please import a JSON data file to begin creating dashboards." />
        ) : (
          <div className="flex flex-col items-center justify-center flex-grow">
            <Loader fontSize={48} color="#36C3ED" />
            {isDataLoading
              ? "Loading project data..."
              : isAutoRunningScript
              ? "Running preprocessing script..."
              : isCodebookLoading
              ? "Loading codebook..."
              : "Loading..."}
          </div>
        )
      ) : isBusyLoading ? (
        <div className="flex flex-col items-center justify-center flex-grow">
          <Loader fontSize={48} color="#36C3ED" />
          {isLoadingConfig
            ? "Loading report configuration..."
            : isDataLoading
            ? "Loading project data..."
            : isAutoRunningScript
            ? "Running preprocessing script..."
            : isCodebookLoading
            ? "Loading codebook..."
            : "Loading..."}
        </div>
      ) : !codebook ? (
        <div className="flex flex-col items-center justify-center flex-grow p-4">
          <p className="text-center text-red-600">
            Could not load codebook. Please check the data or try refreshing.
          </p>
        </div>
      ) : !selectedReport ? (
        <NoReportSelected />
      ) : (
        <div className="flex p-2 h-full flex-grow w-full overflow-hidden">
          {reportingType === "dashboard" ? (
            <ReportingDisplay
              dashboardConfig={config.dashboard}
              onDashboardConfigChange={handleDashboardConfigChange}
              onSlidesForExportUpdate={handleSlidesForExportUpdate} // For PPTX export data
              activeSlideId={activeDashboardSlideId} // Pass down active slide ID
              setActiveSlideId={setActiveDashboardSlideId} // Allow ReportingDisplay to set it
              codebook={codebook}
              queryInstance={queryInstance}
              filter={config.filter}
              tableDataCache={tableDataCache} // Pass cache
              onTableDataUpdate={handleTableDataUpdate} // Pass wrapper function that handles EngineQueryResult
            />
          ) : (
            <ReportTableDisplay
              codebook={codebook}
              queryInstance={queryInstance}
              filter={config.filter}
              ref={excelReportingRef}
              autoRunScriptContent={config.autoRun.script}
              tabsConfig={config.excelReport.tabs}
              onTabsConfigChange={handleExcelReportConfigChange}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default ReportingContainer
